import React, { useState, useEffect, useCallback } from 'react';
import supportTicketsService from '../../services/supportTicketsService';
import './TicketDetailsModal.css';

const TYPE_CONFIG = {
  'Bug': { color: '#ef4444', bg: '#fef2f2', icon: '🐛' },
  'Feature': { color: '#22c55e', bg: '#f0fdf4', icon: '✨' },
  'Improvement': { color: '#3b82f6', bg: '#eff6ff', icon: '💡' },
  'Question': { color: '#a855f7', bg: '#faf5ff', icon: '❓' }
};

const PRIORITY_CONFIG = {
  High: { color: '#ef4444', bg: '#fef2f2' },
  Medium: { color: '#f59e0b', bg: '#fffbeb' },
  Low: { color: '#3b82f6', bg: '#eff6ff' }
};

const STATUS_CONFIG = {
  'Open': { color: '#22c55e', bg: '#f0fdf4' },
  'In Progress': { color: '#f59e0b', bg: '#fffbeb' },
  'Resolved': { color: '#3b82f6', bg: '#eff6ff' },
  'Closed': { color: '#6b7280', bg: '#f9fafb' }
};

const TicketDetailsModal = ({ ticket, isOpen, onClose, onSuccess, canAdmin, statuses }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [ticketData, setTicketData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch ticket details
  const fetchTicketDetails = useCallback(async () => {
    if (!ticket?.id) return;

    setLoading(true);
    try {
      const response = await supportTicketsService.getTicket(ticket.id);
      setTicketData(response.ticket);
      setAttachments(response.attachments || []);
      setResolution(response.ticket.resolution || '');
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticket?.id]);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!ticket?.id) return;

    try {
      const response = await supportTicketsService.getComments(ticket.id);
      setComments(response.comments || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [ticket?.id]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    if (!ticket?.id) return;

    try {
      const response = await supportTicketsService.getHistory(ticket.id);
      setHistory(response.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, [ticket?.id]);

  useEffect(() => {
    if (isOpen && ticket?.id) {
      fetchTicketDetails();
      fetchComments();
      fetchHistory();
    }
  }, [isOpen, ticket?.id, fetchTicketDetails, fetchComments, fetchHistory]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('details');
      setTicketData(null);
      setAttachments([]);
      setComments([]);
      setHistory([]);
      setNewComment('');
      setResolution('');
      setError(null);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailBody('');
    }
  }, [isOpen]);

  const handleStatusChange = async (newStatus) => {
    if (!canAdmin || statusUpdating) return;

    setStatusUpdating(true);
    setError(null);

    try {
      await supportTicketsService.updateStatus(
        ticket.id,
        newStatus,
        newStatus === 'Resolved' ? resolution : null
      );
      await fetchTicketDetails();
      await fetchHistory();
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    setSubmittingComment(true);
    setError(null);

    try {
      await supportTicketsService.addComment(ticket.id, newComment.trim());
      setNewComment('');
      await fetchComments();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailSubject.trim() || !emailBody.trim() || sendingEmail) return;

    setSendingEmail(true);
    setError(null);

    try {
      await supportTicketsService.emailCustomer(ticket.id, {
        subject: emailSubject.trim(),
        body: emailBody.trim()
      });
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailBody('');
      await fetchComments();
      setActiveTab('comments');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to email customer');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await supportTicketsService.deleteAttachment(attachmentId);
      await fetchTicketDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType?.includes('word')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
    return '📎';
  };

  if (!isOpen) return null;

  const data = ticketData || ticket;
  const typeConfig = TYPE_CONFIG[data?.ticket_type] || TYPE_CONFIG.Bug;
  const priorityConfig = PRIORITY_CONFIG[data?.priority] || PRIORITY_CONFIG.Medium;
  const statusConfig = STATUS_CONFIG[data?.status] || STATUS_CONFIG.Open;

  return (
    <div className="std-modal-overlay active" onClick={onClose}>
      <div className="std-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="std-modal-header">
          <div className="std-header-top">
            <span className="std-ticket-number">{data?.ticket_number}</span>
            <button className="std-modal-close-btn" onClick={onClose}>&times;</button>
          </div>
          <h2 className="std-ticket-subject">{data?.subject}</h2>
          <div className="std-ticket-badges">
            <span className="std-badge" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
              {typeConfig.icon} {data?.ticket_type}
            </span>
            <span className="std-badge" style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}>
              {data?.priority} Priority
            </span>
            <span className="std-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
              {data?.status}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="std-error-message">{error}</div>}

        {/* Tabs */}
        <div className="std-tabs">
          <button
            className={`std-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`std-tab ${activeTab === 'comments' ? 'active' : ''}`}
            onClick={() => setActiveTab('comments')}
          >
            Comments ({comments.length})
          </button>
          <button
            className={`std-tab ${activeTab === 'attachments' ? 'active' : ''}`}
            onClick={() => setActiveTab('attachments')}
          >
            Attachments ({attachments.length})
          </button>
          <button
            className={`std-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {/* Modal Body */}
        <div className="std-modal-body">
          {loading ? (
            <div className="std-loading">
              <div className="std-loading-spinner" />
              <p>Loading ticket details...</p>
            </div>
          ) : (
            <>
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="std-details-tab">
                  {data?.source === 'checkout_error' && (
                    <div className="std-customer-report">
                      <h4>🛒 Customer Report — {data?.store_slug || 'general'}</h4>
                      <div className="std-detail-grid">
                        <div className="std-detail-item">
                          <label>Name</label>
                          <span>{data?.customer_name || 'Not provided'}</span>
                        </div>
                        <div className="std-detail-item">
                          <label>Email</label>
                          <span>{data?.customer_email || 'Not provided'}</span>
                        </div>
                        <div className="std-detail-item">
                          <label>Phone</label>
                          <span>{data?.customer_phone || 'Not provided'}</span>
                        </div>
                      </div>
                      {data?.customer_email && (
                        <button
                          className="std-btn-primary std-email-customer-btn"
                          onClick={() => {
                            setShowEmailForm(!showEmailForm);
                            if (!emailSubject) setEmailSubject(`Re: Your order on fitness1stsports.com (${data.ticket_number})`);
                          }}
                        >
                          {showEmailForm ? 'Cancel' : 'Email Customer'}
                        </button>
                      )}
                      {showEmailForm && (
                        <form className="std-email-customer-form" onSubmit={handleSendEmail}>
                          <label>Subject</label>
                          <input
                            type="text"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            placeholder="Subject"
                          />
                          <label>Message</label>
                          <textarea
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            placeholder={`Hi ${data?.customer_name || 'there'}, we noticed you ran into an issue checking out...`}
                            rows="5"
                          />
                          <button
                            type="submit"
                            className="std-btn-primary"
                            disabled={!emailSubject.trim() || !emailBody.trim() || sendingEmail}
                          >
                            {sendingEmail ? 'Sending...' : `Send to ${data.customer_email}`}
                          </button>
                        </form>
                      )}
                    </div>
                  )}

                  <div className="std-detail-section">
                    <h4>Description</h4>
                    <p className="std-description">{data?.description || 'No description provided'}</p>
                  </div>

                  <div className="std-detail-grid">
                    <div className="std-detail-item">
                      <label>Submitted By</label>
                      <span>{data?.submitted_by_name}</span>
                    </div>
                    <div className="std-detail-item">
                      <label>Assigned To</label>
                      <span>{data?.assigned_to_name || 'Unassigned'}</span>
                    </div>
                    <div className="std-detail-item">
                      <label>Created</label>
                      <span>{formatDate(data?.created_at)}</span>
                    </div>
                    <div className="std-detail-item">
                      <label>Last Updated</label>
                      <span>{formatDate(data?.updated_at)}</span>
                    </div>
                  </div>

                  {data?.status === 'Resolved' && data?.resolution && (
                    <div className="std-detail-section">
                      <h4>Resolution</h4>
                      <p className="std-resolution">{data.resolution}</p>
                      {data?.resolved_by_name && (
                        <p className="std-resolved-info">
                          Resolved by {data.resolved_by_name} on {formatDate(data.resolved_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {canAdmin && data?.status !== 'Closed' && (
                    <div className="std-status-section">
                      <h4>Update Status</h4>
                      {data?.status !== 'Resolved' && (
                        <div className="std-resolution-input">
                          <label>Resolution Notes (required when resolving)</label>
                          <textarea
                            value={resolution}
                            onChange={(e) => setResolution(e.target.value)}
                            placeholder="Describe how the issue was resolved..."
                            rows="3"
                          />
                        </div>
                      )}
                      <div className="std-status-buttons">
                        {statuses.map(status => (
                          <button
                            key={status}
                            className={`std-status-btn ${data?.status === status ? 'current' : ''}`}
                            onClick={() => handleStatusChange(status)}
                            disabled={
                              data?.status === status ||
                              statusUpdating ||
                              (status === 'Resolved' && !resolution.trim())
                            }
                            style={{
                              '--status-color': STATUS_CONFIG[status]?.color,
                              '--status-bg': STATUS_CONFIG[status]?.bg
                            }}
                          >
                            {statusUpdating && data?.status !== status ? 'Updating...' : status}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <div className="std-comments-tab">
                  <div className="std-comments-list">
                    {comments.length === 0 ? (
                      <div className="std-empty">No comments yet</div>
                    ) : (
                      comments.map(comment => (
                        <div key={comment.id} className={`std-comment ${comment.is_customer ? 'std-comment-customer' : ''}`}>
                          <div className="std-comment-header">
                            <span className="std-comment-author">
                              {comment.is_customer && <span className="std-customer-tag">Customer</span>}
                              {comment.is_customer ? (data?.customer_name || 'Customer') : comment.created_by_name}
                            </span>
                            <span className="std-comment-date">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="std-comment-text">{comment.comment}</p>
                        </div>
                      ))
                    )}
                  </div>

                  <form className="std-add-comment" onSubmit={handleAddComment}>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      rows="3"
                    />
                    <button
                      type="submit"
                      className="std-btn-primary"
                      disabled={!newComment.trim() || submittingComment}
                    >
                      {submittingComment ? 'Adding...' : 'Add Comment'}
                    </button>
                  </form>
                </div>
              )}

              {/* Attachments Tab */}
              {activeTab === 'attachments' && (
                <div className="std-attachments-tab">
                  {attachments.length === 0 ? (
                    <div className="std-empty">No attachments</div>
                  ) : (
                    <div className="std-attachments-list">
                      {attachments.map(att => (
                        <div key={att.id} className="std-attachment-item">
                          <span className="std-attachment-icon">{getFileIcon(att.mime_type)}</span>
                          <div className="std-attachment-info">
                            <span className="std-attachment-name">{att.original_filename}</span>
                            <span className="std-attachment-meta">
                              {formatFileSize(att.file_size)} • Uploaded by {att.uploaded_by_name}
                            </span>
                          </div>
                          <div className="std-attachment-actions">
                            <a
                              href={att.file_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="std-btn-download"
                              title="Download"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </a>
                            {canAdmin && (
                              <button
                                className="std-btn-delete"
                                onClick={() => handleDeleteAttachment(att.id)}
                                title="Delete"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="std-history-tab">
                  {history.length === 0 ? (
                    <div className="std-empty">No history available</div>
                  ) : (
                    <div className="std-history-list">
                      {history.map(item => (
                        <div key={item.id} className="std-history-item">
                          <div className="std-history-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                          </div>
                          <div className="std-history-content">
                            <p>
                              <strong>{item.changed_by_name}</strong>
                              {item.previous_status ? (
                                <> changed status from <span className="std-history-status">{item.previous_status}</span> to </>
                              ) : (
                                <> set status to </>
                              )}
                              <span className="std-history-status">{item.new_status}</span>
                            </p>
                            {item.notes && <p className="std-history-notes">{item.notes}</p>}
                            <span className="std-history-date">{formatDate(item.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="std-modal-footer">
          <button className="std-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;
